import { mainTextLimits } from "configs/limits";
import { SortOrder, updateSort } from "helpers/updateSort";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";
import {
  handlePaginate,
  type MainText,
  handleSelectAll as handleSelectAllItems,
  handleSelect as handleSelectItem,
  getMainTextList,
  deleteMainTextItem,
  bunchUpdateMainText,
} from "store/cms/footer/mainTexts/slice";
import CmsMainTextFilter from "./components/CmsMainTextFilter";
import { ModalTypes } from "configs/types";
import { openModal } from "store/modals/slice";
import { CmsMainTextHeader } from "./components/CmsMainTextHeader";
import { Table } from "components/table/Table";
import Pagination from "components/Pagination";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { useRefreshList } from "helpers/useRefreshList";

export const MainTexList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [limit, setLimit] = useState<number>(mainTextLimits[0]);
  const [filterByTitle, setFilterByTitle] = useState<string>("");
  const [filterByGeo, setFilterByGeo] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [filterByActive, setFilterByActive] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [order, setOrder] = useState<SortOrder<Omit<MainText, "items">>>({});

  const mainTextsList = useAppSelector((state) => state["cms-main-texts"].list);

  const isMainTextsListFetched = useAppSelector(
    (state) => state["cms-main-texts"].isListFetched,
  );
  const totalItems = useAppSelector(
    (state) => state["cms-main-texts"].totalItems,
  );
  const totalPages = useAppSelector(
    (state) => state["cms-main-texts"].totalPages,
  );
  const activePage = useAppSelector(
    (state) => state["cms-main-texts"].activePage,
  );
  const selectedItems = useAppSelector(
    (state) => state["cms-main-texts"].selectedItems,
  );
  const processingItemId = useAppSelector(
    (state) => state["cms-main-texts"].processingItemId,
  );

  const actionOptions = [
    { label: "Activate", value: "activate" },
    { label: "Hide", value: "hide" },
  ];

  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'cms-main-texts',
    isListFetched: isMainTextsListFetched
  });

  const permissions = usePermissionHelper();

  useEffect(() => {
    if (!isMainTextsListFetched) {
      dispatch(
        getMainTextList({
          limit,
          offset: activePage - 1,
          title: filterByTitle,
          countries: filterByGeo.map((item) => item.value) as string[],
          isActive: getActiveValue(filterByActive),
          order,
        }),
      );
    }
  }, [isMainTextsListFetched, activePage, limit]);

  const handleOrderClick = (key: keyof typeof order) => {
    setOrder((currentOrder) => updateSort(currentOrder, key));
    dispatch(handlePaginate(1));
  };

  const handleSelectAll = () => {
    dispatch(handleSelectAllItems());
  };

  const handleSelect = (id: number) => {
    dispatch(handleSelectItem(id));
  };

  const handlePreviousClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };

  const handleNextClick = () => {
    if (activePage < totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };

  const handleApplyFilter = () => {
    dispatch(handlePaginate(1));
  };

  const handleOpenCreateModal = () => {
    navigate(`${location.pathname}?activeModal=${ModalTypes.mainText}`);

    dispatch(openModal({ activeModal: ModalTypes.mainText }));
  };

  const handleEditCreateModal = (id: MainText["id"]) => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.mainText}&entityId=${id}`,
    );

    dispatch(openModal({ activeModal: ModalTypes.mainText }));
  };

  const getActiveValue = (
    active: { value: string | number; label: string }[],
  ) => {
    if (!active.length || active.length > 1) return undefined;

    return +active[0]?.value;
  };

  const handleDelete = (id: MainText["id"]) => {
    if (!permissions?.delete) {
      return;
    }

    dispatch(deleteMainTextItem(id));
  };

  const handleSelectAction = async (value: string) => {
    if (!selectedItems.length) return;

    const isActive = value === "activate" ? 1 : 0;

    await dispatch(
      bunchUpdateMainText({
        ids: selectedItems,
        isActive,
      }),
    );
  };

  const actionProps = permissions?.update
  ? {
      onEdit: (id: number) => handleEditCreateModal(id),
      onView: undefined,
    }
  : {
      onEdit: undefined,
      onView: (id: number) => handleEditCreateModal(id),
    };

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <CmsMainTextFilter
        filterByTitle={filterByTitle}
        onFilterSearchByTitle={setFilterByTitle}
        filterByActive={filterByActive}
        onFilterByActive={setFilterByActive}
        filterByGeo={filterByGeo}
        onFilterByGeo={setFilterByGeo}
        handleApply={handleApplyFilter}
      />
      <div className="page-list--with-filter-content">
        <CmsMainTextHeader
          options={actionOptions}
          selectAction={handleSelectAction}
          createModal={handleOpenCreateModal}
          isDisabled={selectedItems.length === 0 || !permissions?.update}
          isCreateAllowed={permissions?.create}
          refresh={refresh}
          isRefreshRequested={isRefreshRequested}
        />

        {!isMainTextsListFetched && (
          <>
            <DataTableSkeleton columnsCount={7} rowsCount={8} />
            <PaginationSkeleton />
          </>
        )}

        {isMainTextsListFetched && mainTextsList.length === 0 && (
          <div className="game-table-wrap">
            <EmptyState message={t("noMainTextFound")} />
          </div>
        )}

        {isMainTextsListFetched && mainTextsList.length > 0 && (
          <div className="game-table-wrap">
            <Table
              data={mainTextsList}
              idField="id"
              order={order}
              onAllSelect={handleSelectAll}
              onSelect={handleSelect}
              selectedItems={selectedItems}
              processingId={processingItemId}
              onDelete={handleDelete}
              columns={{
                columnOrder: ["id", "title", "geo", "translations", "isActive"],
                sortingColumns: {
                  geo: {
                    value: "geo",
                    hasSorting: true,
                  },
                },
              }}
              onSort={(v) => {
                const k = v as unknown;
                handleOrderClick(k as keyof typeof order);
              }}
              hasDelete={permissions?.delete}
              hasEdit={permissions?.update}
              hasView={permissions?.read && !permissions?.update}
              hasOptions={permissions?.read || permissions?.update}
              {...actionProps}
            />
          </div>
        )}

        {isMainTextsListFetched && mainTextsList.length > 0 && (
          <Pagination
            handleLimitClick={(e) => {
              setLimit(e);
              dispatch(handlePaginate(1));
            }}
            limitsArray={mainTextLimits}
            activePage={activePage}
            limit={limit}
            totalItems={totalItems}
            totalPages={totalPages}
            handleNextClick={handleNextClick}
            handlePrevClick={handlePreviousClick}
          />
        )}
      </div>
    </div>
  );
};

export default MainTexList;
